import React, { useState, useEffect } from "react";
import { Modal, Select, message, Space, Tag, Button, Popconfirm } from "antd";
import { UserOutlined, DeleteOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { WebApiCustomListsDtoConnectedMemberDto, WebApiMemberDtoMemberDto } from "../../Repository/eventbookyapi";
import { CustomListItemService } from "../../Services/CustomListItemService";
import { TextService } from "../../Services/TextService";
import { LookupRelationType } from "../../Types/LookupRelationType";

interface ManageConnectedMembersModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    customListItemId: number;
    customListId: number;
    connectedMembers: WebApiCustomListsDtoConnectedMemberDto[];
    allMembers: WebApiMemberDtoMemberDto[];
    lookupRelationType: LookupRelationType;
}

const ManageConnectedMembersModal: React.FC<ManageConnectedMembersModalProps> = ({
    open,
    onClose,
    onSuccess,
    customListItemId,
    customListId,
    connectedMembers,
    allMembers,
    lookupRelationType
}) => {
    const GetText = TextService.GetText;
    const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);

    // For Many-to-One, only allow single member selection
    const isSingleSelection = lookupRelationType === LookupRelationType.OneToOne;

    useEffect(() => {
        if (open) {
            // Initialize with existing connected member IDs
            const existingIds = connectedMembers.map(m => m.memberId!).filter(id => id !== undefined);
            setSelectedMemberIds(existingIds);
        }
    }, [open, connectedMembers]);

    const handleSave = async () => {
        setLoading(true);
        try {
            await CustomListItemService.ChangeCustomListItemMembers(customListItemId, {
                customListId,
                memberIds: selectedMemberIds
            });
            message.success(GetText("CustomList_MembersUpdated"));
            onSuccess();
            onClose();
        } catch (error) {
            console.error("Failed to update members:", error);
            message.error(GetText("CustomList_FailedToUpdateMembers"));
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveMember = (memberId: number) => {
        setSelectedMemberIds(prev => prev.filter(id => id !== memberId));
    };

    const handleRemoveAll = () => {
        setSelectedMemberIds([]);
    };

    // Filter out members who are already selected
    const availableMembers = allMembers.filter(m =>
        !selectedMemberIds.includes(m.memberId!)
    );

    return (
        <Modal
            title={<><UserOutlined /> {GetText("CustomList_ManageConnectedMembers")}</>}
            open={open}
            onOk={handleSave}
            onCancel={onClose}
            okText={GetText("Common_Save")}
            cancelText={GetText("Common_Cancel")}
            confirmLoading={loading}
            width={600}
        >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                {/* Current Connected Members */}
                <div>
                    <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong>{GetText("CustomList_CurrentlyConnected")}:</strong>
                        {selectedMemberIds.length > 0 && (
                            <Popconfirm
                                title={GetText("CustomList_RemoveAllMembers")}
                                onConfirm={handleRemoveAll}
                                okText={GetText("Common_Yes")}
                                cancelText={GetText("Common_No")}
                            >
                                <Button
                                    size="small"
                                    danger
                                    icon={<CloseCircleOutlined />}
                                >
                                    {GetText("CustomList_RemoveAll")}
                                </Button>
                            </Popconfirm>
                        )}
                    </div>
                    {selectedMemberIds.length > 0 ? (
                        <Space wrap>
                            {selectedMemberIds.map(memberId => {
                                const member = allMembers.find(m => m.memberId === memberId);
                                if (!member) return null;
                                return (
                                    <Tag
                                        key={memberId}
                                        color="blue"
                                        closable
                                        onClose={() => handleRemoveMember(memberId)}
                                        icon={<UserOutlined />}
                                    >
                                        {member.firstName} {member.lastName}
                                    </Tag>
                                );
                            })}
                        </Space>
                    ) : (
                        <div style={{ color: '#999', fontStyle: 'italic' }}>
                            {GetText("CustomList_NoMembersConnected")}
                        </div>
                    )}
                </div>

                {/* Add New Members */}
                <div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>{GetText("CustomList_AddMembers")}:</strong>
                    </div>
                    <Select
                        mode={isSingleSelection ? undefined : "multiple"}
                        style={{ width: '100%' }}
                        placeholder={GetText("CustomList_SearchMembers")}
                        value={isSingleSelection ? (selectedMemberIds[0] ?? undefined) : selectedMemberIds}
                        onChange={(value) => {
                            if (isSingleSelection) {
                                setSelectedMemberIds(value ? [value as number] : []);
                            } else {
                                setSelectedMemberIds(value as number[]);
                            }
                        }}
                        showSearch
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={allMembers.map(member => ({
                            value: member.memberId!,
                            label: `${member.firstName} ${member.lastName}${member.email ? ` (${member.email})` : ''}`
                        }))}
                        maxTagCount="responsive"
                    />
                </div>
            </Space>
        </Modal>
    );
};

export default ManageConnectedMembersModal;
